import {
  Badge,
  Button,
  Card,
  Chip,
  Group,
  Paper,
  Stack,
  Tabs,
  Text,
  Title,
} from "@mantine/core";
import { IconUsersGroup, IconUser } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { client } from "@/api-client";
import { FancyLoader } from "@/components/FancyLoader";
import { GroupClassCard } from "@/components/GroupClassCard";
import { Header } from "@/components/Header";
import { Portrait } from "@/components/Portrait";
import { getCurrentCountry } from "@/utils/country";
import { getSpecialtyLabel } from "@/pages/trainer/profile/common";
import {
  UniversalRoutes,
  OwnerRoutes,
  useNavigator,
  createPath,
} from "@/routers/routes";
import {
  TrainerDetailsSessionTypeOutput,
  type TrainerDetailsSessionTypeTrainerOutput,
  type GroupClassSearchOutput,
} from "@/api-types";
import { useHashTab } from "@/hooks/useHashTab";

export const FindTraining = () => {
  const { activeTab, onTabChange } = useHashTab<string>("individual-sessions");

  const { data: groupClassResults } = client.useQuery(
    "get",
    "/owner/group-classes",
    {
      params: {
        query: {
          country: getCurrentCountry(),
        },
      },
    },
  );

  const { data: individualSessionResults } = client.useQuery(
    "get",
    "/owner/individual-sessions",
    {
      params: {
        query: {
          country: getCurrentCountry(),
        },
      },
    },
  );

  if (!groupClassResults || !individualSessionResults) {
    return <FancyLoader />;
  }

  return (
    <Stack>
      <Header title="Find Training" backButton={true} />

      <Tabs value={activeTab} onChange={onTabChange}>
        <Tabs.List grow>
          <Tabs.Tab
            value="individual-sessions"
            leftSection={<IconUser size="0.8rem" />}
          >
            Individual Sessions
          </Tabs.Tab>
          <Tabs.Tab
            value="group-classes"
            leftSection={<IconUsersGroup size="0.8rem" />}
          >
            Group Classes
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="group-classes" pt="md">
          <GroupClassesList groupClasses={groupClassResults.groupClasses} />
        </Tabs.Panel>

        <Tabs.Panel value="individual-sessions" pt="md">
          <IndividualSessionsList
            sessions={individualSessionResults.individualSessions}
          />
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
};

const GroupClassesList = ({
  groupClasses,
}: {
  groupClasses: GroupClassSearchOutput[];
}) => {
  const [selectedTheme, setSelectedTheme] = useState<string | null>(null);

  if (groupClasses.length === 0) {
    return (
      <Paper p="xl" withBorder>
        <Stack align="center">
          <IconUsersGroup size={48} color="gray" />
          <Title order={4} ta="center">
            No group classes available
          </Title>
          <Text c="dimmed" ta="center">
            No trainers are currently offering open group classes.
          </Text>
        </Stack>
      </Paper>
    );
  }

  // Count group classes by theme for filter pills
  const themeCounts = useMemo(() => {
    const themeCountsLocal: Record<string, number> = {};
    groupClasses.forEach((groupClass) => {
      const themes = groupClass.classType.themes;
      if (!themes || themes.length === 0) {
        themeCountsLocal["other"] = (themeCountsLocal["other"] || 0) + 1;
        return;
      }
      themes.forEach((theme) => {
        themeCountsLocal[theme] = (themeCountsLocal[theme] || 0) + 1;
      });
    });
    return themeCountsLocal;
  }, [groupClasses]);

  // Get available themes sorted alphabetically (Other classes last)
  const availableThemes = Object.keys(themeCounts).sort((a, b) => {
    if (a === "other") return 1;
    if (b === "other") return -1;
    return getSpecialtyLabel(a).localeCompare(getSpecialtyLabel(b));
  });

  // Filter group classes based on selected theme
  const filteredGroupClasses =
    selectedTheme === null
      ? groupClasses
      : groupClasses.filter((groupClass) => {
          const themes = groupClass.classType.themes;

          if (!themes || themes.length === 0) {
            return selectedTheme === "other";
          }

          return themes.includes(selectedTheme);
        });

  // Group filtered classes by theme
  const classesByTheme = filteredGroupClasses.reduce(
    (acc, groupClass) => {
      const themes = groupClass.classType.themes;

      if (!themes || themes.length === 0) {
        if (!acc["other"]) {
          acc["other"] = [];
        }
        acc["other"].push(groupClass);
      } else {
        themes.forEach((theme) => {
          if (!acc[theme]) {
            acc[theme] = [];
          }
          acc[theme].push(groupClass);
        });
      }

      return acc;
    },
    {} as Record<string, GroupClassSearchOutput[]>,
  );

  // Get themes that have classes after filtering
  const activeThemes = Object.keys(classesByTheme).sort((a, b) => {
    if (a === "other") return 1;
    if (b === "other") return -1;
    return getSpecialtyLabel(a).localeCompare(getSpecialtyLabel(b));
  });

  const handleThemeClick = (theme: string) => {
    if (theme === selectedTheme) {
      setSelectedTheme(null);
    } else {
      setSelectedTheme(theme);
    }
  };

  return (
    <Stack gap="lg">
      {/* Filter Chips */}
      <div>
        <Group gap="xs">
          {availableThemes.map((theme) => (
            <Chip
              key={theme}
              checked={selectedTheme === theme}
              onClick={() => handleThemeClick(theme)}
            >
              {theme === "other" ? "Other classes" : getSpecialtyLabel(theme)} (
              {themeCounts[theme]})
            </Chip>
          ))}
        </Group>
        {selectedTheme && (
          <Text size="xs" c="dimmed" mt="xs">
            Showing {filteredGroupClasses.length} classes for "
            {selectedTheme === "other"
              ? "Other classes"
              : getSpecialtyLabel(selectedTheme)}
            "
          </Text>
        )}
      </div>

      {/* Group classes grouped by theme */}
      {filteredGroupClasses.length === 0 ? (
        <Paper p="xl" withBorder>
          <Stack align="center">
            <Text ta="center" c="dimmed">
              No group classes found with the selected themes.
            </Text>
          </Stack>
        </Paper>
      ) : selectedTheme ? (
        // When filtering, show flat list
        <Stack gap="md">
          {filteredGroupClasses.map((groupClass) => (
            <GroupClassCard
              key={groupClass.group.id}
              groupClass={groupClass}
              showJoinButton={true}
            />
          ))}
        </Stack>
      ) : (
        // When not filtering, show grouped by theme
        <Stack gap="xl">
          {activeThemes.map((theme) => (
            <Stack key={theme} gap="md">
              <Title order={3}>
                {theme === "other" ? "Other classes" : getSpecialtyLabel(theme)}
              </Title>
              <Stack gap="md">
                {classesByTheme[theme]?.map((groupClass) => (
                  <GroupClassCard
                    key={groupClass.group.id}
                    groupClass={groupClass}
                    showJoinButton={true}
                  />
                ))}
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

const IndividualSessionsList = ({
  sessions,
}: {
  sessions: TrainerDetailsSessionTypeTrainerOutput[];
}) => {
  const [selectedTheme, setSelectedTheme] = useState<string | null>(null);

  if (sessions.length === 0) {
    return (
      <Paper p="xl" withBorder>
        <Stack align="center">
          <IconUser size={48} color="gray" />
          <Title order={4} ta="center">
            No individual sessions available
          </Title>
          <Text c="dimmed" ta="center">
            No trainers are currently offering individual sessions.
          </Text>
        </Stack>
      </Paper>
    );
  }

  // Count sessions by theme for filter pills
  const themeCounts = sessions.reduce(
    (acc, session) => {
      const themes = session.sessionType.themes;

      if (!themes || themes.length === 0) {
        acc["other"] = (acc["other"] || 0) + 1;
      } else {
        themes.forEach((theme) => {
          acc[theme] = (acc[theme] || 0) + 1;
        });
      }

      return acc;
    },
    {} as Record<string, number>,
  );

  // Get available themes sorted alphabetically (Other sessions last)
  const availableThemes = Object.keys(themeCounts).sort((a, b) => {
    if (a === "other") return 1;
    if (b === "other") return -1;
    return getSpecialtyLabel(a).localeCompare(getSpecialtyLabel(b));
  });

  // Filter sessions based on selected theme
  const filteredSessions =
    selectedTheme === null
      ? sessions
      : sessions.filter((session) => {
          const themes = session.sessionType.themes;

          if (!themes || themes.length === 0) {
            return selectedTheme === "other";
          }

          return themes.includes(selectedTheme);
        });

  // Group filtered sessions by theme
  const sessionsByTheme = filteredSessions.reduce(
    (acc, session) => {
      const themes = session.sessionType.themes;

      if (!themes || themes.length === 0) {
        if (!acc["other"]) {
          acc["other"] = [];
        }
        acc["other"].push(session);
      } else {
        themes.forEach((theme) => {
          if (!acc[theme]) {
            acc[theme] = [];
          }
          acc[theme].push(session);
        });
      }

      return acc;
    },
    {} as Record<string, TrainerDetailsSessionTypeTrainerOutput[]>,
  );

  // Get themes that have sessions after filtering
  const activeThemes = Object.keys(sessionsByTheme).sort((a, b) => {
    if (a === "other") return 1;
    if (b === "other") return -1;
    return getSpecialtyLabel(a).localeCompare(getSpecialtyLabel(b));
  });

  const handleThemeClick = (theme: string) => {
    if (theme === selectedTheme) {
      setSelectedTheme(null);
    } else {
      setSelectedTheme(theme);
    }
  };

  return (
    <Stack gap="lg">
      {/* Filter Chips */}
      <div>
        <Group gap="xs">
          {availableThemes.map((theme) => (
            <Chip
              key={theme}
              checked={selectedTheme === theme}
              onClick={() => handleThemeClick(theme)}
            >
              {theme === "other" ? "Other sessions" : getSpecialtyLabel(theme)}{" "}
              ({themeCounts[theme]})
            </Chip>
          ))}
        </Group>
        {selectedTheme && (
          <Text size="xs" c="dimmed" mt="xs">
            Showing {filteredSessions.length} sessions for &quot;
            {selectedTheme === "other"
              ? "Other sessions"
              : getSpecialtyLabel(selectedTheme)}
            &quot;
          </Text>
        )}
      </div>

      {/* Sessions grouped by theme */}
      {filteredSessions.length === 0 ? (
        <Paper p="xl" withBorder>
          <Stack align="center">
            <Text ta="center" c="dimmed">
              No sessions found with the selected themes.
            </Text>
          </Stack>
        </Paper>
      ) : selectedTheme ? (
        // When filtering, show flat list
        <Stack gap="md">
          {filteredSessions.map((session) => (
            <IndividualSessionCard
              key={`${session.sessionType.id}-${session.trainer.id}`}
              session={session}
            />
          ))}
        </Stack>
      ) : (
        // When not filtering, show grouped by theme
        <Stack gap="xl">
          {activeThemes.map((theme) => (
            <Stack key={theme} gap="md">
              <Title order={3}>
                {theme === "other"
                  ? "Other sessions"
                  : getSpecialtyLabel(theme)}
              </Title>
              <Stack gap="md">
                {sessionsByTheme[theme]?.map((session) => (
                  <IndividualSessionCard
                    key={`${session.sessionType.id}-${session.trainer.id}`}
                    session={session}
                  />
                ))}
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

const IndividualSessionCard = ({
  session,
}: {
  session: TrainerDetailsSessionTypeTrainerOutput;
}) => {
  const { navigator } = useNavigator();

  const handleViewTrainerClick = navigator({
    path: UniversalRoutes.TRAINER,
    params: { trainerId: session.trainer.id },
  });

  const handleRequestClick = navigator({
    path: OwnerRoutes.REQUEST_SESSION,
    params: { trainerId: session.trainer.id },
  });

  return (
    <Card p="md" withBorder style={{ cursor: "pointer" }}>
      <Group align="flex-start" gap="md" style={{ width: "100%" }}>
        {/* Left Column - Session Info */}
        <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
          <div>
            <Title order={4}>{session.sessionType.name}</Title>
            {session.sessionType.description && (
              <Text size="sm" c="dimmed" mt="xs">
                {session.sessionType.description}
              </Text>
            )}
          </div>

          <Group gap="md">
            <Portrait profile={session.trainer} size="md" who="person" />
            <div style={{ flex: 1 }}>
              <Text fw={500}>
                {session.trainer.firstName} {session.trainer.lastName}
              </Text>
              <Text size="sm" c="dimmed">
                {session.trainer.locality}, {session.trainer.country}
              </Text>
            </div>
          </Group>
        </Stack>

        {/* Right Column - Session Details */}
        <Stack gap="sm" style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs">
            <Badge
              variant="light"
              color={session.sessionType.online ? "blue" : "green"}
            >
              {session.sessionType.online ? "Virtual" : "In-person"}
            </Badge>
            <Text size="sm" c="dimmed">
              {session.sessionType.durationMinutes} minutes
            </Text>
          </Group>

          {session.sessionType.price && session.sessionType.price > 0 ? (
            <Text size="sm" fw={500} c="green">
              €{(session.sessionType.price / 100).toFixed(2)}
            </Text>
          ) : (
            <Text size="sm" fw={500} c="dimmed">
              Custom price
            </Text>
          )}
        </Stack>
      </Group>

      <Stack gap="sm" mt="md">
        <Button
          fullWidth
          variant="outline"
          size="sm"
          onClick={handleViewTrainerClick}
        >
          View Trainer Page
        </Button>
        <Button fullWidth onClick={handleRequestClick}>
          Click to Request
        </Button>
      </Stack>
    </Card>
  );
};
